import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Center,
    Flex,
    Grid,
    GridItem,
    HStack,
    IconButton,
    Image,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    Textarea,
    useDisclosure,
    useToast,
    VStack
} from "@chakra-ui/react";
import dayjs from "dayjs";
import useAPI from "hooks/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const STREAM_CDN_BASE = "https://stream-cdn.nightco.io";

function resolveCdnPath(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return `${STREAM_CDN_BASE}/${normalizedPath}`;
}

function getMediaPreviewUrl(media) {
  if (!media) return null;
  if (media.type === "video") {
    return resolveCdnPath(media.thumbnail?.clear);
  }
  return media.clear || null;
}

function getMediaFullUrl(media) {
  if (!media) return null;
  if (media.type === "video") {
    return media.mp4?.["720p"] || null;
  }
  return media.clear || null;
}

function getVideoPosterUrl(media) {
  if (media?.type !== "video") return null;
  return resolveCdnPath(media.thumbnail?.clear);
}

const AspectBox = ({ ratio = "16:9", children }) => {
  const aspect = ratio === "4:3" ? 4 / 3 : ratio === "1:1" ? 1 : 16 / 9;
  return (
    <Box position="relative" width="100%" style={{ aspectRatio: aspect }}>
      <Box position="absolute" inset={0}>
        {children}
      </Box>
    </Box>
  );
};

const PreviewImage = ({ src }) => (
  <Image src={src} alt="" w="100%" h="100%" objectFit="cover" />
);

const PlayButton = () => (
  <Flex
    position="absolute"
    top="50%"
    left="50%"
    transform="translate(-50%, -50%)"
    alignItems="center"
    justifyContent="center"
    pointerEvents="none"
    zIndex={2}
  >
    <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.5)" />
      <polygon
        points="26,20 26,44 46,32"
        fill="#fff"
        style={{ filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))" }}
      />
    </svg>
  </Flex>
);

const MediaPreview = ({ src, isVideo }) => (
  <Box position="relative" w="100%" h="100%">
    <PreviewImage src={src} />
    {isVideo && <PlayButton />}
  </Box>
);

function MediaLightbox({ media = [], isOpen, onClose, startIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  useEffect(() => {
    if (isOpen) setCurrentIndex(startIndex);
  }, [isOpen, startIndex]);

  const currentMedia = media[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < media.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) setCurrentIndex((i) => i - 1);
  }, [hasPrev]);

  const goNext = useCallback(() => {
    if (hasNext) setCurrentIndex((i) => i + 1);
  }, [hasNext]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goPrev, goNext, onClose]);

  if (!currentMedia) return null;

  const isVideo = currentMedia.type === "video";
  const mediaSrc = getMediaFullUrl(currentMedia);
  const posterSrc = isVideo ? getVideoPosterUrl(currentMedia) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="transparent" maxW="100vw" h="100vh" m={0} boxShadow="none">
        <ModalBody p={0} position="relative" display="flex" alignItems="center" justifyContent="center">
          <IconButton
            aria-label="Close"
            onClick={onClose}
            position="absolute"
            top={4}
            right={4}
            zIndex={10}
            rounded="full"
            bg="whiteAlpha.200"
            color="white"
            _hover={{ bg: "whiteAlpha.300" }}
            icon={<span style={{ fontWeight: 800, fontSize: 18 }}>✕</span>}
          />
          {media.length > 1 && (
            <>
              <IconButton
                aria-label="Previous"
                onClick={goPrev}
                isDisabled={!hasPrev}
                position="absolute"
                left={4}
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                rounded="full"
                bg="whiteAlpha.200"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
                _disabled={{ opacity: 0.3, cursor: "not-allowed" }}
                icon={<span style={{ fontWeight: 800, fontSize: 20 }}>←</span>}
              />
              <IconButton
                aria-label="Next"
                onClick={goNext}
                isDisabled={!hasNext}
                position="absolute"
                right={4}
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                rounded="full"
                bg="whiteAlpha.200"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
                _disabled={{ opacity: 0.3, cursor: "not-allowed" }}
                icon={<span style={{ fontWeight: 800, fontSize: 20 }}>→</span>}
              />
            </>
          )}
          {media.length > 1 && (
            <Text
              position="absolute"
              bottom={4}
              left="50%"
              transform="translateX(-50%)"
              color="white"
              fontSize="sm"
              bg="blackAlpha.600"
              px={3}
              py={1}
              rounded="full"
            >
              {currentIndex + 1} / {media.length}
            </Text>
          )}
          <Box maxW="90vw" maxH="90vh" display="flex" alignItems="center" justifyContent="center">
            {isVideo ? (
              <video
                key={mediaSrc}
                src={mediaSrc}
                poster={posterSrc || undefined}
                controls
                autoPlay
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  background: "#000",
                }}
              />
            ) : (
              <img
                src={mediaSrc}
                alt=""
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const Collage = ({ previews = [], onClickIndex }) => {
  const count = previews.length;
  if (count <= 0) return null;

  const handleClick = (idx) => (e) => {
    e.stopPropagation();
    if (onClickIndex) onClickIndex(idx);
  };

  if (count === 1) {
    const p = previews[0];
    return (
      <AspectBox ratio={p?.aspectRatio || "16:9"}>
        <Box cursor="pointer" onClick={handleClick(0)} w="100%" h="100%">
          <MediaPreview src={p?.url} isVideo={p?.type === "video"} />
        </Box>
      </AspectBox>
    );
  }
  return (
    <AspectBox ratio="16:9">
      <Grid templateColumns="repeat(2, 1fr)" templateRows="repeat(2, 1fr)" gap={1} w="100%" h="100%">
        {count === 2 && (
          <>
            <GridItem colSpan={1} rowSpan={2} overflow="hidden" cursor="pointer" onClick={handleClick(0)}>
              <MediaPreview src={previews[0]?.url} isVideo={previews[0]?.type === "video"} />
            </GridItem>
            <GridItem colSpan={1} rowSpan={2} overflow="hidden" cursor="pointer" onClick={handleClick(1)}>
              <MediaPreview src={previews[1]?.url} isVideo={previews[1]?.type === "video"} />
            </GridItem>
          </>
        )}
        {count === 3 && (
          <>
            <GridItem colSpan={1} rowSpan={2} overflow="hidden" cursor="pointer" onClick={handleClick(0)}>
              <MediaPreview src={previews[0]?.url} isVideo={previews[0]?.type === "video"} />
            </GridItem>
            <GridItem colSpan={1} rowSpan={1} overflow="hidden" cursor="pointer" onClick={handleClick(1)}>
              <MediaPreview src={previews[1]?.url} isVideo={previews[1]?.type === "video"} />
            </GridItem>
            <GridItem colSpan={1} rowSpan={1} overflow="hidden" cursor="pointer" onClick={handleClick(2)}>
              <MediaPreview src={previews[2]?.url} isVideo={previews[2]?.type === "video"} />
            </GridItem>
          </>
        )}
        {count >= 4 && (
          <>
            <GridItem overflow="hidden" cursor="pointer" onClick={handleClick(0)}>
              <MediaPreview src={previews[0]?.url} isVideo={previews[0]?.type === "video"} />
            </GridItem>
            <GridItem overflow="hidden" cursor="pointer" onClick={handleClick(1)}>
              <MediaPreview src={previews[1]?.url} isVideo={previews[1]?.type === "video"} />
            </GridItem>
            <GridItem overflow="hidden" cursor="pointer" onClick={handleClick(2)}>
              <MediaPreview src={previews[2]?.url} isVideo={previews[2]?.type === "video"} />
            </GridItem>
            <GridItem overflow="hidden" position="relative" cursor="pointer" onClick={handleClick(3)}>
              <MediaPreview src={previews[3]?.url} isVideo={previews[3]?.type === "video"} />
              {count > 4 && (
                <Flex
                  position="absolute"
                  inset={0}
                  bg="rgba(0,0,0,0.35)"
                  align="center"
                  justify="center"
                  pointerEvents="none"
                  zIndex={3}
                >
                  <Text fontWeight="bold" fontSize="xl" color="white">+{count - 4}</Text>
                </Flex>
              )}
            </GridItem>
          </>
        )}
      </Grid>
    </AspectBox>
  );
};

function PostCard({ post, onDeny }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const publishTime = useMemo(() => {
    if (!post?.publishedAt && !post?.createdAt) return null;
    const raw =
      typeof post?.publishedAt === "number" || typeof post?.publishedAt === "string"
        ? post.publishedAt
        : post?.publishedAt?._seconds;
    const fallback =
      typeof post?.createdAt === "number" || typeof post?.createdAt === "string"
        ? post.createdAt
        : post?.createdAt?._seconds;
    const value = raw ?? fallback;
    if (!value) return null;
    if (typeof value === "string") {
      return dayjs(value).format("MMM D, HH:mm");
    }
    if (typeof value === "number") {
      const date = value > 1e12 ? dayjs(value) : dayjs.unix(value);
      return date.format("MMM D, HH:mm");
    }
    return null;
  }, [post?.publishedAt, post?.createdAt]);

  const mediaItems = useMemo(() => post?.media || [], [post?.media]);

  const previews = useMemo(() => {
    if (mediaItems.length === 0) return [];
    return mediaItems.slice(0, 4).map((item) => {
      const url = getMediaPreviewUrl(item);
      return {
        url: url || undefined,
        aspectRatio: item.aspectRatio || "16:9",
        type: item.type,
      };
    });
  }, [mediaItems]);

  const status = post?.status;

  return (
    <Box
      borderWidth="1px"
      borderColor="rgba(255,255,255,0.08)"
      rounded="md"
      overflow="hidden"
      bg="#0f0f0f"
    >
      <Box p={4} pb={3}>
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            {status && (
              <Badge
                colorScheme={
                  status === "published"
                    ? "green"
                    : status === "processing"
                      ? "yellow"
                      : status === "denied"
                        ? "red"
                        : "gray"
                }
                textTransform="capitalize"
              >
                {status}
              </Badge>
            )}
            {publishTime && (
              <Text fontSize="xs" color="gray.500">
                {publishTime}
              </Text>
            )}
          </HStack>
        </Flex>
      </Box>

      {status === "denied" && post?.deniedReason && (
        <Box px={4} pb={3}>
          <Box
            bg="red.900"
            border="1px solid"
            borderColor="red.700"
            rounded="md"
            px={3}
            py={2}
          >
            <Text fontSize="xs" color="red.300" fontWeight="semibold" mb={1}>
              拒絕原因
            </Text>
            <Text fontSize="sm" color="red.100">
              {post.deniedReason}
            </Text>
          </Box>
        </Box>
      )}

      {post?.text && (
        <Box px={4} pt={2} pb={2}>
          <Text noOfLines={3} color="white" fontSize="sm" wordBreak="break-word">
            {post.text}
          </Text>
        </Box>
      )}

      {previews.length > 0 && (
        <Collage previews={previews} onClickIndex={openLightbox} />
      )}

      <MediaLightbox
        media={mediaItems}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        startIndex={lightboxIndex}
      />

      <Box p={4}>
        <Flex align="center" justify="space-between">
          <HStack spacing={4} fontSize="sm" color="gray.400">
            <Text>{mediaItems.length} media</Text>
            <Text>❤️ {post?.likedCount || 0}</Text>
            <Text>🔖 {post?.savedCount || 0}</Text>
          </HStack>
          {status !== "denied" && (
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => onDeny(post)}
            >
              拒絕
            </Button>
          )}
        </Flex>
      </Box>
    </Box>
  );
}

export const PostsList = ({ creatorId }) => {
  const api = useAPI();
  const toast = useToast();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const bottomDetectorRef = useRef(null);

  const [denyingPost, setDenyingPost] = useState(null);
  const {
    isOpen: isDenyOpen,
    onOpen: onDenyOpen,
    onClose: onDenyClose,
  } = useDisclosure();
  const [denyReason, setDenyReason] = useState("");

  const loadPosts = useCallback(
    async (cursor = null) => {
      try {
        if (cursor) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }

        console.log("[PostsList] Calling api.getCreatorPosts for:", creatorId);
        const res = await api.getCreatorPosts(creatorId, cursor ? { next: cursor, limit: 20 } : { limit: 20 });
        console.log("[PostsList] Response:", res);
        const newPosts = res?.data || [];
        const next = res?.paginator?.next || res?.next || null;

        if (cursor) {
          setPosts((prev) => [...prev, ...newPosts]);
        } else {
          setPosts(newPosts);
        }
        setNextCursor(next);
      } catch (err) {
        console.error("Error loading posts:", err);
        toast({
          title: "載入失敗",
          description: err.message || "請稍後再試",
          status: "error",
        });
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [api, creatorId, toast],
  );

  useEffect(() => {
    console.log("[PostsList] useEffect triggered, creatorId:", creatorId);
    if (creatorId) {
      loadPosts();
    }
  }, [creatorId]); // eslint-disable-line

  useEffect(() => {
    if (bottomDetectorRef.current && nextCursor) {
      const intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].intersectionRatio >= 1 && !isLoadingMore) {
          loadPosts(nextCursor);
        }
      });
      intersectionObserver.observe(bottomDetectorRef.current);
      return () => {
        intersectionObserver.disconnect();
      };
    }
  }, [bottomDetectorRef, nextCursor, isLoadingMore]); // eslint-disable-line

  const handleDeny = useCallback(
    (post) => {
      setDenyingPost(post);
      setDenyReason("");
      onDenyOpen();
    },
    [onDenyOpen],
  );

  const handleDenyConfirm = useCallback(async () => {
    if (!denyingPost || !denyReason.trim()) {
      toast({
        title: "請輸入拒絕原因",
        status: "warning",
      });
      return;
    }

    try {
      await api.denyPost(denyingPost.id, denyReason.trim());
      toast({
        title: "已拒絕該貼文",
        status: "success",
      });
      loadPosts();
      onDenyClose();
      setDenyingPost(null);
      setDenyReason("");
    } catch (error) {
      console.error("Error denying post:", error);
      toast({
        title: "拒絕失敗",
        description: error.message || "請稍後再試",
        status: "error",
      });
    }
  }, [denyingPost, denyReason, api, toast, loadPosts, onDenyClose]);

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box p={4}>
      {posts.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          此創作者尚未發布任何貼文
        </Alert>
      ) : (
        <VStack spacing={4} align="stretch">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onDeny={handleDeny} />
          ))}
        </VStack>
      )}

      <Box
        id="bottom-detector"
        height="1px"
        ref={bottomDetectorRef}
        width="full"
      />

      {isLoadingMore && (
        <Center py={4}>
          <Spinner />
        </Center>
      )}

      <Modal isOpen={isDenyOpen} onClose={onDenyClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>拒絕貼文</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text color="gray.300">
                確定要拒絕此貼文嗎？此操作將標記貼文為已拒絕狀態。
              </Text>
              <Textarea
                placeholder="請輸入拒絕原因..."
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                minH="100px"
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDenyClose}>
              取消
            </Button>
            <Button colorScheme="red" onClick={handleDenyConfirm}>
              確認拒絕
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

